import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { SHIPPING_TIERS } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  "Excavators",
  "Cranes",
  "Backhoe Loaders",
  "Mine Vehicles",
  "Filters & Fluids",
  "Vehicle Spare Parts",
  "Mining Equipment",
  "Anything Else",
];

const fieldClass =
  "rounded-none bg-transparent border-0 border-b border-border px-0 h-12 text-base focus-visible:ring-0 focus-visible:border-primary transition-colors";

export const QuoteForm = ({ open, onClose, initialCategory }) => {
  const empty = { name: "", phone: "", email: "", category: "", shipping: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm(() => ({ ...empty, category: initialCategory || "" }));
    }
  }, [open, initialCategory]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.category || form.message.trim().length < 3) {
      toast.error("Please fill in your name, phone, category and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        category: form.category,
        shipping: form.shipping || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quote-modal"
          className="fixed inset-0 z-[100] flex items-stretch md:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full md:max-w-xl bg-card border border-border max-h-[100svh] md:max-h-[92svh] overflow-y-auto no-scrollbar"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-8 py-5 border-b border-border bg-card">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Request A Quote</p>
                <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">Tell Us What You Need</h3>
              </div>
              <button
                data-testid="quote-close-btn"
                onClick={onClose}
                className="h-10 w-10 grid place-items-center border border-border hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {done ? (
              <div data-testid="quote-success" className="px-6 md:px-8 py-16 text-center">
                <CheckCircle2 size={48} className="mx-auto text-primary" />
                <h4 className="mt-6 font-heading text-3xl font-bold tracking-tight">Request Received</h4>
                <p className="mt-3 font-body text-muted-foreground max-w-sm mx-auto">
                  Thank you, {form.name.split(" ")[0] || "there"}. Our team will reach out with a quote as soon as possible.
                </p>
                <button
                  onClick={onClose}
                  className="mt-8 h-12 px-8 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] font-semibold hover:bg-foreground transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="px-6 md:px-8 py-8 space-y-7">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="q-name" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Full Name *</Label>
                    <Input id="q-name" data-testid="quote-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="John Banda" className={fieldClass} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="q-phone" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Phone *</Label>
                    <Input id="q-phone" data-testid="quote-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+260 ..." className={fieldClass} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="q-email" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Email (optional)</Label>
                  <Input id="q-email" data-testid="quote-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className={fieldClass} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Category *</Label>
                    <Select value={form.category} onValueChange={(v) => v && set("category", v)}>
                      <SelectTrigger data-testid="quote-category" className="rounded-none bg-transparent border-0 border-b border-border px-0 h-12 focus:ring-0">
                        <SelectValue placeholder="Select a category">
                          {form.category || undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {CATEGORY_OPTIONS.map((c) => (
                          <SelectItem key={c} value={c} className="rounded-none font-mono text-sm">{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Shipping (optional)</Label>
                    <Select value={form.shipping} onValueChange={(v) => v && set("shipping", v)}>
                      <SelectTrigger data-testid="quote-shipping" className="rounded-none bg-transparent border-0 border-b border-border px-0 h-12 focus:ring-0">
                        <SelectValue placeholder="Preferred shipping">
                          {form.shipping || undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {SHIPPING_TIERS.map((t) => (
                          <SelectItem key={t.code} value={`${t.name} (${t.time})`} className="rounded-none font-mono text-sm">
                            {t.name} — {t.time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="q-message" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">What do you need? *</Label>
                  <Textarea id="q-message" data-testid="quote-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="Describe the parts, machinery, quantities or vehicle make/model..." className="rounded-none bg-transparent border border-border focus-visible:ring-0 focus-visible:border-primary resize-none" />
                </div>

                <button
                  type="submit"
                  data-testid="quote-submit"
                  disabled={loading}
                  className="w-full h-14 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] font-semibold hover:bg-foreground transition-colors duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : "Send Request"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
